/**
 * BrightPath - useBudgets Hook
 * BP-04-04: Expenses Frontend
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { budgetsApi } from '../api';
import type {
  Budget,
  BudgetCreate,
  BudgetUpdate,
  BudgetListParams,
  BudgetStatus,
  BudgetSummary,
  BudgetTrend,
} from '../types';

// Query keys
export const budgetKeys = {
  all: ['budgets'] as const,
  lists: () => [...budgetKeys.all, 'list'] as const,
  list: (familyId: string, params?: BudgetListParams) =>
    [...budgetKeys.lists(), familyId, params] as const,
  details: () => [...budgetKeys.all, 'detail'] as const,
  detail: (budgetId: string) => [...budgetKeys.details(), budgetId] as const,
  status: (familyId: string, params?: { year?: number; month?: number }) =>
    [...budgetKeys.all, 'status', familyId, params] as const,
  summary: (familyId: string, params?: { year?: number; month?: number }) =>
    [...budgetKeys.all, 'summary', familyId, params] as const,
  trends: (familyId: string, params?: { months?: number }) =>
    [...budgetKeys.all, 'trends', familyId, params] as const,
};

/**
 * Hook for fetching budgets list
 */
export function useBudgets(familyId: string, params?: BudgetListParams) {
  return useQuery({
    queryKey: budgetKeys.list(familyId, params),
    queryFn: () => budgetsApi.getBudgets(familyId, params),
    enabled: !!familyId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook for fetching a single budget
 */
export function useBudget(budgetId: string) {
  return useQuery({
    queryKey: budgetKeys.detail(budgetId),
    queryFn: () => budgetsApi.getBudget(budgetId),
    enabled: !!budgetId,
  });
}

/**
 * Hook for budget status (budget vs actual)
 */
export function useBudgetStatus(
  familyId: string,
  params?: {
    year?: number;
    month?: number;
  }
) {
  return useQuery({
    queryKey: budgetKeys.status(familyId, params),
    queryFn: () => budgetsApi.getBudgetStatus(familyId, params),
    enabled: !!familyId,
    staleTime: 30 * 1000, // 30 seconds - budget status changes with expenses
  });
}

/**
 * Hook for budget summary
 */
export function useBudgetSummary(
  familyId: string,
  params?: {
    year?: number;
    month?: number;
  }
) {
  return useQuery({
    queryKey: budgetKeys.summary(familyId, params),
    queryFn: () => budgetsApi.getBudgetSummary(familyId, params),
    enabled: !!familyId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for budget trends over time
 */
export function useBudgetTrends(
  familyId: string,
  params?: {
    months?: number;
  }
) {
  return useQuery({
    queryKey: budgetKeys.trends(familyId, params),
    queryFn: () => budgetsApi.getBudgetTrends(familyId, params),
    enabled: !!familyId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for creating a budget
 */
export function useCreateBudget(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BudgetCreate) => budgetsApi.createBudget(familyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
    },
  });
}

/**
 * Hook for updating a budget
 */
export function useUpdateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, data }: { budgetId: string; data: BudgetUpdate }) =>
      budgetsApi.updateBudget(budgetId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(variables.budgetId) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
    },
  });
}

/**
 * Hook for deleting a budget
 */
export function useDeleteBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (budgetId: string) => budgetsApi.deleteBudget(budgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
    },
  });
}

/**
 * Hook for copying budgets to another month
 */
export function useCopyBudgets(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fromYear,
      fromMonth,
      toYear,
      toMonth,
    }: {
      fromYear: number;
      fromMonth: number;
      toYear: number;
      toMonth: number;
    }) => budgetsApi.copyBudgets(familyId, fromYear, fromMonth, toYear, toMonth),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
    },
  });
}

/**
 * Get current budget period helper
 */
export function useCurrentBudgetPeriod(): { year: number; month: number } {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  };
}
